import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button, Input, Label, toast } from "@/components/ds";

/**
 * Admin recovery for a locked-out account.
 *
 * Without this there is no way back in: someone who loses their authenticator cannot sign in
 * (a code is required) and cannot turn two-factor off (that needs a session, and is refused
 * outright for roles where two-factor is mandatory).
 *
 * The server decides who may do this. A platform admin cannot reset the senior admin, and nobody
 * can reset themselves here. Every reset is audited and ends the target's existing sessions.
 */
export function MfaRecoveryCard() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [done, setDone] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => api.internalResetMfa(email.trim(), reason.trim()),
    onSuccess: (d) => {
      setDone(d.email);
      setEmail("");
      setReason("");
      toast.success("Two-factor cleared");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const ready = email.trim().length > 3 && reason.trim().length >= 3 && !reset.isPending;

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid="mfa-recovery-card">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-100">
        <KeyRound className="h-4 w-4 text-amber-400" /> Two-factor recovery
      </p>
      <p className="mt-1 text-xs text-slate-400">
        Clear two-factor for someone who has lost their authenticator app. They keep their password
        and will be asked to set two-factor up again next time they sign in. Their current sessions
        are ended immediately.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="mfa-reset-email">Account email</Label>
          <Input
            id="mfa-reset-email"
            data-testid="mfa-reset-email"
            type="email"
            placeholder="name@business.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="mfa-reset-reason">Reason (recorded in the audit log)</Label>
          <Input
            id="mfa-reset-reason"
            data-testid="mfa-reset-reason"
            placeholder="Lost phone, confirmed by call"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>

      <Button
        className="mt-3"
        variant="secondary"
        data-testid="mfa-reset-submit"
        disabled={!ready}
        onClick={() => reset.mutate()}
      >
        {reset.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Clear two-factor
      </Button>

      {done && (
        <p className="mt-3 text-xs text-emerald-400" data-testid="mfa-reset-done">
          Two-factor cleared for {done}. They can sign in with their password and will be prompted
          to set it up again.
        </p>
      )}
    </div>
  );
}

export default MfaRecoveryCard;
